// Root.tsx — new top-level component that replaces the one in index.tsx.
//
// Architecture: a single source of truth for "which screen am I on?" lives in
// the URL hash via useRoute(). Pre-auth, Portal handles login / signup / reset
// / password-recovery as it always did. Post-auth, NavBar floats on top of
// whichever screen the current route picks. Each screen is now pure content —
// no more in-screen back buttons / sign-out / language toggles.

import React from "react";
import { useAuth } from "../AuthProvider";
import { useRoute } from "./useRoute";

import NavBar from "./NavBar";
import LaunchPad from "./LaunchPad";
import MyAccount from "./MyAccount";

import Portal, { WavyBackground } from "../Portal";
import App from "../App";
import FormulaCompare from "../FormulaCompare";
import GoldenRatioAnalyzer from "../GoldenRatioAnalyzer";
import DatabaseModule from "../DatabaseModule";
import AdminPanel from "../AdminPanel";

export default function Root() {
  const { user, loading, recovering, isAdmin } = useAuth();
  const [route] = useRoute();

  // Loading splash
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0a0618",
          color: "#a78bfa",
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        Loading…
      </div>
    );
  }

  // Pre-auth or password recovery → Portal handles the flow (login, signup,
  // reset, recovery, "Set New Password"). Portal returns its own full layout.
  if (recovering || !user) {
    return <Portal />;
  }

  // Authenticated — pick the right screen for the current route.
  // Note: most screens are now rendered WITHOUT an onBack prop. They've been
  // stripped of their back buttons; nav handles up-level navigation.
  let screen: React.ReactNode;
  switch (route) {
    case "launchpad":
      screen = <LaunchPad />;
      break;
    case "app":
      screen = <App key="app" initialTab="formulation" />;
      break;
    case "packaging":
      // SAME key as "app" so React keeps the App component mounted when
      // switching between the formulation and packaging tabs via the unified
      // nav. Otherwise the App tree unmounts and all in-progress formulation
      // edits, ingredient picks, ratios etc. are lost.
      screen = <App key="app" initialTab="packaging" />;
      break;
    case "compare":
      screen = <FormulaCompare />;
      break;
    case "goldenratio":
      screen = <GoldenRatioAnalyzer />;
      break;
    case "database":
      screen = <DatabaseModule key="db-home" initialView="home" />;
      break;
    case "ingredients":
      screen = <DatabaseModule key="db-ing" initialView="ingredients" />;
      break;
    case "formulas":
      screen = <DatabaseModule key="db-form" initialView="formulas" />;
      break;
    case "account":
      screen = <MyAccount />;
      break;
    case "admin":
      // Belt-and-suspenders — the admin nav button is hidden for non-admins,
      // but if someone deep-links to #admin we silently fall back.
      screen = isAdmin ? <AdminPanel /> : <LaunchPad />;
      break;
    default:
      screen = <LaunchPad />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a0618",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        position: "relative",
      }}
    >
      {/* Global wavy backdrop — fixed position, sits behind every screen.
          Matches the prototype which renders #wavy as a sibling of body content. */}
      <WavyBackground />
      <NavBar />
      {/* Top padding so screen content clears the floating nav. Each screen
          can override this if it wants edge-to-edge content. */}
      <div style={{ paddingTop: 96, position: "relative", zIndex: 1 }}>{screen}</div>
    </div>
  );
}
